import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { Button } from './Button';
import { useT } from '../lib/i18n';
import { cn } from '../lib/cn';

/**
 * Empty, loading, error and toast states.
 *
 * Loading is a skeleton of the real layout, never a spinner: the page should
 * not move once the data lands. Empty states say what to do next rather than
 * announcing that there is nothing here.
 */

export function EmptyState({
  title,
  body,
  action,
  className,
}: {
  title: string;
  body: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-2 rounded-panel border border-dashed border-line-strong p-[18px]',
        className,
      )}
    >
      <span className="text-row font-semibold">{title}</span>
      <span className="text-pretty text-meta text-muted">{body}</span>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title,
  body,
  onRetry,
  className,
}: {
  title?: string;
  body?: string;
  onRetry?: () => void;
  className?: string;
}) {
  const t = useT();
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-2 rounded-panel border border-dashed border-alert-line bg-alert-tint p-[18px]',
        className,
      )}
      role="alert"
    >
      <span className="text-row font-semibold text-alert-on-tint">{title ?? t('error.generic')}</span>
      {body && <span className="text-pretty text-meta text-muted">{body}</span>}
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-1">
          {t('action.retry')}
        </Button>
      )}
    </div>
  );
}

/** A skeleton block. Give it the size of the thing it stands in for. */
export function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return <div className={cn('skeleton', className)} style={style} aria-hidden />;
}

/** Rows of the real height, so nothing shifts when the data arrives. */
export function SkeletonRows({ rows = 6, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col', className)} aria-busy role="status">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex h-row items-center gap-3 border-b border-line px-[22px]">
          <Skeleton className="h-3.5 flex-1" style={{ maxWidth: `${60 - (i % 4) * 8}%` }} />
          <Skeleton className="h-[22px] w-[110px] rounded-pill" />
          <Skeleton className="h-3.5 w-[64px]" />
        </div>
      ))}
    </div>
  );
}

// ---------- toasts ----------

type ToastTone = 'default' | 'alert';

interface ToastMessage {
  id: number;
  message: string;
  tone: ToastTone;
  /** Undo, for anything that removed something. */
  action?: { label: string; onClick: () => void };
}

interface ToastApi {
  /** The verb in the button becomes the verb in the toast. */
  show: (message: string, options?: { tone?: ToastTone; action?: ToastMessage['action'] }) => void;
}

const ToastContext = createContext<ToastApi>({ show: () => undefined });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const show = useCallback<ToastApi['show']>((message, options) => {
    setMessages((current) => [
      ...current,
      { id: Date.now() + Math.random(), message, tone: options?.tone ?? 'default', action: options?.action },
    ]);
  }, []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {messages.map((toast) => (
          <ToastPrimitive.Root
            key={toast.id}
            onOpenChange={(open) => {
              if (!open) setMessages((current) => current.filter((m) => m.id !== toast.id));
            }}
            className={cn(
              'flex items-center gap-3 rounded-pill py-2.5 pl-[18px] pr-2.5 shadow-menu',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out',
              // The alert toast is fixed red, so its label is fixed white:
              // on-ink follows the tenant's ink and would flip to near-black.
              toast.tone === 'alert' ? 'bg-alert text-white' : 'bg-ink text-on-ink',
            )}
          >
            <ToastPrimitive.Title className="text-body font-medium">
              {toast.message}
            </ToastPrimitive.Title>
            {toast.action && (
              <ToastPrimitive.Action
                altText={toast.action.label}
                onClick={toast.action.onClick}
                className="rounded-pill px-3 py-1 text-chip font-semibold text-on-ink/70 transition-colors duration-base ease-emphasis hover:bg-on-ink/10 hover:text-on-ink"
              >
                {toast.action.label}
              </ToastPrimitive.Action>
            )}
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-5 right-5 z-50 flex w-auto flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
